import axios from 'axios';
import { getAdminPanelClient } from '@/http/client';
import { loadStorageItem, removeStorageItem, saveStorageItem } from '@/services/storage';

const ACCESS_TOKEN = 'admin-panel:access';
const REFRESH_TOKEN = 'admin-panel:refresh';
const SELECTED_MANAGEMENT_COMPANY = 'admin-panel:selected-management-company';
const AUTH_URL = '/auth';
const CLIENTS_URL = '/clients/';
const MANAGEMENT_COMPANIES_URL = '/management-companies/';
const SELECT_MANAGEMENT_COMPANIES_URL = '/select/management-companies/';
const MANAGEMENT_COMPANY_EMPLOYEES_URL = '/management-company-employees/';
const STAFF_USERS_URL = '/staff-users/';
const ROLES_URL = '/roles/';

function normalizeListResponse(data) {
  if (Array.isArray(data)) return data;

  return data?.results || [];
}

function readSelectedManagementCompanyFromStorage() {
  const storedValue = loadStorageItem(SELECTED_MANAGEMENT_COMPANY);

  if (!storedValue) return null;

  try {
    const parsedValue = JSON.parse(storedValue);

    if (!parsedValue?.id) return null;

    return parsedValue;
  } catch {
    return null;
  }
}

class AdminPanelService {
  constructor() {
    this.client = getAdminPanelClient();
    this.access = loadStorageItem(ACCESS_TOKEN);
    this.refresh = loadStorageItem(REFRESH_TOKEN);
    this.refreshPromise = null;
    this.selectedManagementCompany = readSelectedManagementCompanyFromStorage();
    this.isRedirectingToLogin = false;

    this.client.addRequestInterceptor({
      request: config => {
        if (!this.access) return config;

        return {
          ...config,
          headers: {
            ...config.headers,
            Authorization: `Bearer ${this.access}`,
          },
        };
      },
    });

    this.client.addResponseInterceptor({
      error: error => {
        if (axios.isCancel(error)) {
          return Promise.reject(error);
        }

        const originalRequest = error?.config;
        const status = error?.response?.status;
        const requestUrl = originalRequest?.url || '';
        const isRefreshRequest = requestUrl.includes(`${AUTH_URL}/token/refresh/`);
        const isLoginRequest = requestUrl.includes(`${AUTH_URL}/token/obtain/`);

        if (status !== 401 || !originalRequest) {
          return Promise.reject(error);
        }

        if (isLoginRequest) {
          return Promise.reject(error);
        }

        if (isRefreshRequest || originalRequest.isRetryRequest || !this.refresh) {
          this.handleAuthExpired();
          return Promise.reject(error);
        }

        originalRequest.isRetryRequest = true;

        if (!this.refreshPromise) {
          this.refreshPromise = this.refreshAuthTokens()
            .catch(refreshError => {
              this.handleAuthExpired();
              throw refreshError;
            })
            .finally(() => {
              this.refreshPromise = null;
            });
        }

        return this.refreshPromise.then(() => this.client.request(originalRequest));
      },
    });
  }

  getCancelToken() {
    return this.client.getCancelToken();
  }

  setAuthTokens({ access, refresh }) {
    this.access = access;
    this.refresh = refresh;

    if (access) saveStorageItem(ACCESS_TOKEN, access);
    else removeStorageItem(ACCESS_TOKEN);

    if (refresh) saveStorageItem(REFRESH_TOKEN, refresh);
    else removeStorageItem(REFRESH_TOKEN);
  }

  hasAuthTokens() {
    return Boolean(this.access);
  }

  removeAuthTokens() {
    this.access = null;
    this.refresh = null;
    removeStorageItem(ACCESS_TOKEN);
    removeStorageItem(REFRESH_TOKEN);
  }

  handleAuthExpired() {
    this.removeAuthTokens();
    this.redirectToLogin();
  }

  redirectToLogin() {
    if (this.isRedirectingToLogin) return;

    const loginPath = '/admin-panel/login';

    if (typeof window === 'undefined') return;
    if (window.location.pathname === loginPath) return;

    this.isRedirectingToLogin = true;
    window.location.assign(loginPath);
  }

  login(email, password) {
    return this.client.post(`${AUTH_URL}/token/obtain/`, { email, password }).then(data => {
      this.setAuthTokens(data);
      return data;
    });
  }

  refreshAuthTokens() {
    return this.client.post(`${AUTH_URL}/token/refresh/`, { refresh: this.refresh }).then(tokens => {
      this.setAuthTokens({ refresh: this.refresh, ...tokens });
      return tokens;
    });
  }

  logout() {
    this.removeAuthTokens();
  }

  sendResetEmail(email) {
    return this.client.post(`${AUTH_URL}/password/reset/start/`, { email });
  }

  checkResetToken(token) {
    return this.client
      .post(`${AUTH_URL}/password/reset/check/`, { data: token })
      .then(data => data.userId || data.user_id);
  }

  updatePassword(userId, token, password) {
    return this.client.patch(`${AUTH_URL}/password/reset/${userId}/`, {
      data: token,
      password1: password,
      password2: password,
    });
  }

  getProfile(config = {}) {
    return this.client.get('/me/', config);
  }

  getSelectedManagementCompany() {
    return this.selectedManagementCompany;
  }

  setSelectedManagementCompany(company) {
    if (!company?.id) {
      this.selectedManagementCompany = null;
      removeStorageItem(SELECTED_MANAGEMENT_COMPANY);
      return null;
    }

    const value = {
      id: company.id,
      name: company.name,
      ...(company.subdomain ? { subdomain: company.subdomain } : {}),
    };

    this.selectedManagementCompany = value;
    saveStorageItem(SELECTED_MANAGEMENT_COMPANY, JSON.stringify(value));

    return value;
  }

  getManagementCompanies(params = {}, config = {}) {
    return this.client.get(MANAGEMENT_COMPANIES_URL, { params, ...config }).then(data => {
      if (Array.isArray(data)) {
        return { results: data, count: data.length };
      }

      return {
        results: data?.results || [],
        count: data?.count ?? (data?.results || []).length,
      };
    });
  }

  getManagementCompaniesSelect(params = {}, config = {}) {
    return this.client.get(SELECT_MANAGEMENT_COMPANIES_URL, { params, ...config }).then(normalizeListResponse);
  }

  getClients(params = {}, config = {}) {
    return this.client.get(CLIENTS_URL, { params, ...config });
  }

  getClient(clientId, config = {}) {
    return this.client.get(`${CLIENTS_URL}${clientId}/`, config);
  }

  getManagementCompanyEmployees(params = {}, config = {}) {
    return this.client.get(MANAGEMENT_COMPANY_EMPLOYEES_URL, { params, ...config });
  }

  getManagementCompanyEmployee(employeeId, config = {}) {
    return this.client.get(`${MANAGEMENT_COMPANY_EMPLOYEES_URL}${employeeId}/`, config);
  }

  getStaffUsers(params = {}, config = {}) {
    return this.client.get(STAFF_USERS_URL, { params, ...config });
  }

  createStaffUser(data) {
    return this.client.post(STAFF_USERS_URL, data);
  }

  updateStaffUser(userId, data) {
    return this.client.patch(`${STAFF_USERS_URL}${userId}/`, data);
  }

  archiveStaffUser(userId) {
    return this.client.post(`${STAFF_USERS_URL}${userId}/archive/`, {});
  }

  getRoles(params = {}, config = {}) {
    return this.client.get(ROLES_URL, { params, ...config });
  }
}

export default new AdminPanelService();
